import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ToasterType } from '@web/components/toaster/toaster';
import { AccountRoleOption } from '@packages/models/api';
import { accountsService, feedbackAdminService } from '@web/services/singletons';
import { Button, Icon, Label, LoadingCircle, TextInputHelper, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useContentOverlayState } from '@web/core/hooks/use-content-overlay';
import colors from '@packages/core/styles/colors';
import ReactDOM from 'react-dom';
import Select from '@web/components/select';

const useStyles = createUseStyles({
    panel: {
        backgroundColor: colors.white,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 390,
        position: 'absolute',
        right: 0,
        top: 0,
        width: '100%',
        zIndex: 5,
    },
    panelHeader: {
        alignItems: 'center',
        backgroundColor: colors.grayTwo,
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        height: 56,
        justifyContent: 'space-between',
        padding: '0px 24px',
    },
    panelScrollContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: 24,
    },
    panelFooter: {
        borderTop: `1px solid ${colors.grayThree}`,
        backgroundColor: colors.white,
        padding: '16px 24px',
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
    },
    actionButtons: {},
    closeButton: { '&:hover': { cursor: 'pointer' } },
    divider: { height: 1, backgroundColor: colors.grayThree },
    firstSection: { marginBottom: 24 },
    lastDivider: { marginTop: 'auto!important' },
    section: { marginBottom: 24, marginTop: 24 },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(255,255,255,0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
});

const styles = StyleSheet.create({
    label: { fontSize: 11, fontWeight: '700', lineHeight: 16 },
    headerTitle: { fontSize: 15, fontWeight: '400', lineHeight: 24 },
});

const Divider: FC<{ className?: string }> = ({ className }) => <hr className={className} />;

interface AddUserPanelProps {
    setShowToast: (toast: { show: boolean; message: string; type?: ToasterType }) => void;
}

export const AddUserPanel: FC<AddUserPanelProps> = ({ setShowToast }) => {
    const classes = useStyles();
    const { dismissAllOverlays } = useContentOverlayState();

    const [isLoading, setIsLoading] = useState(false);

    const [baseRoleOptions, setBaseRoleOptions] = useState<AccountRoleOption[]>([]);
    const [addOnRoleOptions, setAddOnRoleOptions] = useState<AccountRoleOption[]>([]);

    const [subaruUserName, setSubaruUserName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [baseRoles, setBaseRoles] = useState<AccountRoleOption[]>([]);
    const [addOnRoles, setAddOnRoles] = useState<AccountRoleOption[]>([]);

    useEffect(() => {
        const fetchRoles = async () => {
            setIsLoading(true);
            try {
                const { success, data } = await feedbackAdminService.fetchAccountRoles();
                if (!success) throw data;
                setBaseRoleOptions(data.baseRoles);
                setAddOnRoleOptions(data.addOnRoles);
            } catch (error: any) {
                setShowToast({ show: true, message: error.message, type: 'error' });
            } finally {
                setIsLoading(false);
            }
        };
        fetchRoles();
    }, []);

    const handleSaveButtonClick = useCallback(async () => {
        setIsLoading(true);
        try {
            const payload = {
                soaUsername: subaruUserName,
                firstName,
                lastName,
                emailAddress,
                roleTargetTypeId: 'GLOBAL',
                baseRole: baseRoles[0]?.roleId,
                addOnRole: addOnRoles.map((role) => role.roleId),
                jobTitle,
            };

            const response = await accountsService.createAccount(payload);
            if (response.success) {
                setShowToast({
                    show: true,
                    message: `User ${subaruUserName} created successfully`,
                    type: 'success',
                });
                dismissAllOverlays();
            } else {
                throw response.data;
            }
        } catch (error: any) {
            setShowToast({ show: true, message: error.message, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [subaruUserName, firstName, lastName, emailAddress, baseRoles, addOnRoles, dismissAllOverlays]);

    const isCreateDisabled = useMemo(() => {
        return (
            !subaruUserName.trim() ||
            !firstName.trim() ||
            !lastName.trim() ||
            !emailAddress.trim() ||
            baseRoles.length === 0
        );
    }, [subaruUserName, firstName, lastName, emailAddress, baseRoles]);

    const rightPanelDiv = document.getElementById('right-side-panel');
    if (!rightPanelDiv) return null;

    return ReactDOM.createPortal(
        <div className={classes.panel}>
            {isLoading && (
                <div className={classes.loadingOverlay}>
                    <LoadingCircle size={64} borderWidth={8} borderColor={colors.grayOne} />
                </div>
            )}

            <div className={classes.panelHeader}>
                <Typography style={styles.headerTitle}>Add a new user manually to TechShare</Typography>
                <div className={classes.closeButton} onClick={dismissAllOverlays}>
                    <Icon name="x-close" />
                </div>
            </div>

            <div className={classes.panelScrollContainer}>
                <Divider className={classes.divider} />
                <div className={classes.firstSection}>
                    <TextInputHelper
                        label="Subaru Username"
                        value={subaruUserName}
                        labelStyle={styles.label}
                        onChangeText={setSubaruUserName}
                        placeholder="Enter Subaru username"
                        required
                    />
                </div>
                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <TextInputHelper
                        label=" First Name"
                        value={firstName}
                        labelStyle={styles.label}
                        onChangeText={setFirstName}
                        placeholder="Enter First Name"
                        required
                    />
                </div>
                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <TextInputHelper
                        label="Last Name"
                        value={lastName}
                        labelStyle={styles.label}
                        onChangeText={setLastName}
                        placeholder="Enter Last Name"
                        required
                    />
                </div>
                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <TextInputHelper
                        label="Email"
                        value={emailAddress}
                        labelStyle={styles.label}
                        onChangeText={setEmailAddress}
                        placeholder="Enter email"
                        required
                        type="email"
                    />
                </div>
                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <TextInputHelper
                        label="Job Title"
                        value={jobTitle}
                        labelStyle={styles.label}
                        onChangeText={setJobTitle}
                        placeholder="Enter Job Title"
                    />
                </div>
                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <Label style={styles.label} required>
                        Role
                    </Label>
                    <Select
                        value={baseRoles[0]?.roleId}
                        options={[
                            { value: 'SELECT', title: 'SELECT' },
                            ...baseRoleOptions.map((option) => ({
                                value: option.roleId,
                                title: option.description,
                            })),
                        ]}
                        onChange={(event) => {
                            const selectedRole = baseRoleOptions.filter(
                                (option) => option.roleId === event.currentTarget.value
                            );
                            setBaseRoles(selectedRole);
                        }}
                        required
                    />
                </div>
                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <Label style={styles.label}>Add On Role</Label>
                    <Typeahead
                        id="manage-users-async-typeahead"
                        placeholder="Search for roles..."
                        labelKey="description"
                        multiple
                        selected={addOnRoles}
                        onChange={setAddOnRoles}
                        options={addOnRoleOptions}
                    />
                </div>
                <Divider className={`${classes.divider} ${classes.lastDivider}`} />
            </div>

            <div className={classes.panelFooter}>
                <div className={`d-flex flex-row align-items-center justify-content-between ${classes.actionButtons}`}>
                    <Button onPress={handleSaveButtonClick} disabled={isCreateDisabled}>
                        Create User
                    </Button>
                    <Button variant="ghost-blue" onPress={dismissAllOverlays}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>,
        rightPanelDiv
    );
};
